import { WorkflowRole } from '@exam-management/common/types';
import { TaskStatus } from '@exam-management/graphql';
import { Box, EditOutlinedIcon, Grid, IconButton, Text } from '@shared/components';
import { formatDate } from '@shared/utils';
import { Fragment, type ReactNode, useMemo, useState } from 'react';

import { useQSetReviewTask } from '../../providers';
import { EditTaskDueDateModal } from '../edit-task-due-date-modal';

export default function NewQSetReviewTaskDetailsView() {
  const {
    mainTaskDetail: { name, dueDate, assignee, subtasks, owner, status },
    assignedRoles,
  } = useQSetReviewTask();

  const [isEditDueDateModalOpen, setIsEditDueDateModalOpen] = useState(false);

  const assigneeName = assignee ? `${assignee.firstname} ${assignee.lastname}` : '-';

  const ownerName = owner ? `${owner.firstname} ${owner.lastname}` : '-';

  const formattedDueDate = dueDate ? formatDate(dueDate) : '-';

  const isOwner = assignedRoles.includes(WorkflowRole.TASK_OWNER);
  const canEditDueDate = isOwner && status !== TaskStatus.Completed;

  const reviewers = useMemo(() => {
    const reviewerNames = subtasks.map(
      (subtask) => `${subtask.assignee.firstname} ${subtask.assignee.lastname}`,
    );
    const uniqueReviewerNames = Array.from(new Set(reviewerNames));
    return uniqueReviewerNames.length > 3
      ? `${uniqueReviewerNames.slice(0, 3).join(', ')}, ...`
      : uniqueReviewerNames.join(', ');
  }, [subtasks]);

  const dueDateValue = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Text variant='body1Semibold'>{formattedDueDate}</Text>
      {canEditDueDate ? (
        <IconButton onClick={() => setIsEditDueDateModalOpen(true)}>
          <EditOutlinedIcon
            sx={{
              color: (theme) => theme.palette.grey[600],
            }}
          />
        </IconButton>
      ) : null}
    </Box>
  );

  const taskInfo: {
    label: string;
    value: ReactNode;
  }[] = useMemo(() => {
    const baseInfo = [
      {
        label: 'Task Name:',
        value: name,
      },
      {
        label: 'Task Owner:',
        value: ownerName,
      },
      ...(owner.id !== assignee.id
        ? [
            {
              label: 'Lead Reviewer:',
              value: assigneeName,
            },
          ]
        : []),
      {
        label: 'Reviewer:',
        value: reviewers || '-',
      },
      {
        label: 'Due Date:',
        value: dueDateValue,
      },
    ];

    return baseInfo;
  }, [name, assigneeName, dueDateValue, ownerName, reviewers]);

  return (
    <>
      <Grid
        sx={(theme) => ({
          gridTemplateColumns: '120px 1fr',
          rowGap: theme.spacing(2),
          columnGap: theme.spacing(6),
        })}
      >
        {taskInfo.map((info) => (
          <Fragment key={info.label}>
            <Text>{info.label}</Text>
            {typeof info.value === 'object' ? (
              info.value
            ) : (
              <Text variant='body1Semibold'>{info.value}</Text>
            )}
          </Fragment>
        ))}
      </Grid>
      <EditTaskDueDateModal
        open={isEditDueDateModalOpen}
        onClose={() => setIsEditDueDateModalOpen(false)}
      />
    </>
  );
}
